/**
 * Suite media gallery – selects slideshow slides for colour swatches.
 */

/**
 * @param {string | undefined} raw
 * @returns {string}
 */
function decodePreviewUrl(raw) {
  return raw?.trim().replace(/&amp;/g, '&') ?? '';
}

/**
 * @param {HTMLElement} control
 * @returns {SuiteMediaGallery | null}
 */
export function findSuiteGallery(control) {
  const section = control.closest('.shopify-section');
  const picker = control.closest('suite-colour-picker');
  const suiteId = picker?.dataset.currentSuiteId;

  if (suiteId) {
    const byId = document.querySelector(`suite-media-gallery[data-suite-id="${suiteId}"]`);
    if (byId && typeof byId.selectForSwatch === 'function') {
      return /** @type {SuiteMediaGallery} */ (byId);
    }
  }

  const local = section?.querySelector('suite-media-gallery');
  if (local && typeof local.selectForSwatch === 'function') {
    return /** @type {SuiteMediaGallery} */ (local);
  }

  const pageGallery = document.querySelector('suite-media-gallery');
  if (pageGallery && typeof pageGallery.selectForSwatch === 'function') {
    return /** @type {SuiteMediaGallery} */ (pageGallery);
  }

  return null;
}

class SuiteMediaGallery extends HTMLElement {
  connectedCallback() {
    this.bindSwatches();
  }

  bindSwatches() {
    this.#bindSectionSwatches();
  }

  /**
   * @returns {import('./slideshow.js').Slideshow | null}
   */
  #getSlideshow() {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      return /** @type {import('./slideshow.js').Slideshow} */ (slideshow);
    }
    return null;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow?.refs?.slides?.length) {
      return [...slideshow.refs.slides].filter((slide) => slide.hasAttribute('slide-id'));
    }
    return [...this.querySelectorAll('slideshow-slide[slide-id]')];
  }

  /**
   * @param {string} url
   */
  #imageKey(url) {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      let filename = parsed.pathname.split('/').pop()?.split('?')[0] ?? '';
      filename = filename.replace(/_\d+x(?:\d+x)?(?=\.[^.]+$)/i, '');
      return filename.toLowerCase();
    } catch {
      let filename = url.split('/').pop()?.split('?')[0] ?? '';
      filename = filename.replace(/_\d+x(?:\d+x)?(?=\.[^.]+$)/i, '');
      return filename.toLowerCase();
    }
  }

  /**
   * @param {import('./slideshow.js').Slideshow} slideshow
   * @param {number} matchIndex
   */
  #selectSlideAtIndex(slideshow, matchIndex) {
    if (matchIndex < 0) return false;

    const slides = this.#getSlides();
    const slide = slides[matchIndex];
    const slideId = slide?.getAttribute('slide-id');

    if (slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return true;
    }

    slideshow.select(matchIndex, undefined, { animate: false });
    return true;
  }

  /**
   * @param {string} previewUrl
   */
  #swapActiveSlideImage(previewUrl) {
    if (!previewUrl) return false;

    const slides = this.#getSlides();
    const activeSlide =
      slides.find((slide) => slide.getAttribute('aria-hidden') === 'false') ?? slides[0];
    const img = activeSlide?.querySelector('img');

    if (!(img instanceof HTMLImageElement)) return false;

    img.src = previewUrl;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    return true;
  }

  /**
   * @param {HTMLElement} control
   */
  selectForSwatch(control) {
    const slideshow = this.#getSlideshow();
    const gallerySlideIndex = control.dataset.suiteGallerySlideIndex;
    const colourId = control.dataset.suiteColourId;
    const previewUrl = decodePreviewUrl(control.dataset.suitePreviewImage);
    const previewImageId =
      control.dataset.suitePreviewImageId || control.dataset.suiteSlideId;
    const slides = this.#getSlides();
    let slideChanged = false;

    if (slideshow) {
      if (colourId) {
        const match = slides.find(
          (slide) => slide.getAttribute('data-suite-colour-id') === colourId
        );
        if (match) {
          const slideId = match.getAttribute('slide-id');
          if (slideId) {
            slideshow.select({ id: slideId }, undefined, { animate: false });
            slideChanged = true;
          }
        }
      }

      if (!slideChanged && gallerySlideIndex !== undefined && gallerySlideIndex !== '') {
        const index = parseInt(gallerySlideIndex, 10);
        if (!Number.isNaN(index)) {
          slideChanged = this.#selectSlideAtIndex(slideshow, index);
        }
      }

      if (!slideChanged && previewImageId) {
        const idString = String(previewImageId);
        const matchIndex = slides.findIndex((slide) => {
          const slideId = slide.getAttribute('slide-id');
          return slideId != null && String(slideId) === idString;
        });
        slideChanged = this.#selectSlideAtIndex(slideshow, matchIndex);
      }

      if (!slideChanged && previewUrl) {
        const previewKey = this.#imageKey(previewUrl);
        if (previewKey) {
          const matchIndex = slides.findIndex((slide) => {
            const slideUrl =
              slide.getAttribute('data-suite-preview-url') ||
              slide.querySelector('img')?.currentSrc ||
              slide.querySelector('img')?.src ||
              '';
            const slideKey = this.#imageKey(slideUrl);
            return (
              slideKey === previewKey ||
              slideKey.includes(previewKey) ||
              previewKey.includes(slideKey)
            );
          });
          slideChanged = this.#selectSlideAtIndex(slideshow, matchIndex);
        }
      }
    }

    if (!slideChanged && previewUrl) {
      return this.#swapActiveSlideImage(previewUrl);
    }

    return slideChanged;
  }

  #bindSectionSwatches() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const bind = () => {
      section.querySelectorAll('.suite-colour-picker__swatch').forEach((control) => {
        if (!(control instanceof HTMLElement)) return;
        if (control.dataset.suiteGalleryBound === 'true') return;
        control.dataset.suiteGalleryBound = 'true';
      });
    };

    bind();

    const picker = section.querySelector('suite-colour-picker');
    if (picker) {
      const observer = new MutationObserver(bind);
      observer.observe(picker, { childList: true, subtree: true });
    }
  }
}

if (!customElements.get('suite-media-gallery')) {
  customElements.define('suite-media-gallery', SuiteMediaGallery);
}

export { SuiteMediaGallery };
